import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, SUBJECTS, CLASSES } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import VideoPlayerModal from "@/components/VideoPlayerModal";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Play, Bookmark, Search, Clock } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

export default function BrowseLearnings() {
  const [sp, setSp] = useSearchParams();
  const { user } = useAuth();
  const [cls, setCls] = useState(sp.get("class") || "");
  const [subject, setSubject] = useState(sp.get("subject") || "");
  const [q, setQ] = useState("");
  const [videos, setVideos] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [progress, setProgress] = useState({});
  const [bookmarks, setBookmarks] = useState(new Set());

  useEffect(() => {
    const params = {};
    if (cls) params.class_level = cls;
    if (subject) params.subject = subject;
    if (q) params.q = q;
    api.get("/videos", { params }).then(r => setVideos(r.data));
    const next = {};
    if (cls) next.class = cls;
    if (subject) next.subject = subject;
    setSp(next, { replace: true });
  }, [cls, subject, q, setSp]);

  useEffect(() => {
    if (!user) return;
    api.get("/progress/videos").then(r => {
      const p = {};
      r.data.forEach(x => { p[x.video_id] = x; });
      setProgress(p);
    }).catch((err) => console.error("progress load failed", err));
    api.get("/bookmarks").then(r => {
      setBookmarks(new Set(r.data.filter(b => b.resource_type === "video").map(b => b.resource_id)));
    }).catch((err) => console.error("bookmarks load failed", err));
  }, [user]);

  const openVideo = async (v) => {
    setPlaying(v);
    if (user) {
      try {
        await api.post("/progress/video", { video_id: v.video_id, position_seconds: 30, duration_seconds: v.duration_seconds || 1800 });
        setProgress(prev => ({ ...prev, [v.video_id]: { position_seconds: 30, duration_seconds: v.duration_seconds || 1800 } }));
      } catch (err) {
        console.error("progress update failed", err);
      }
    }
  };

  const toggleBm = async (id) => {
    if (!user) return toast.error("Sign in to bookmark");
    try {
      const r = await api.post("/bookmarks/toggle", { resource_type: "video", resource_id: id });
      setBookmarks(prev => {
        const s = new Set(prev);
        if (r.data.status === "added") s.add(id); else s.delete(id);
        return s;
      });
    } catch (err) {
      console.error("bookmark toggle failed", err);
    }
  };

  const resumeList = Object.values(progress).slice(0, 4);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 pt-32 pb-16">
        <div className="text-xs uppercase tracking-widest font-semibold text-secondary mb-3">Video learnings</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Lessons that click, then stick.</h1>
        <p className="text-muted-foreground mt-3">Watch, pause, resume — we remember exactly where you left off.</p>

        {resumeList.length > 0 && (
          <div className="mt-10">
            <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-4">Continue watching</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="resume-list">
              {resumeList.map(p => (
                <button key={p.video_id} onClick={() => openVideo(p.video)}
                  data-testid={`resume-${p.video_id}`}
                  className="glass rounded-2xl overflow-hidden card-3d text-left">
                  <div className="relative aspect-video bg-muted">
                    {p.video?.thumbnail && <img src={p.video.thumbnail} alt="" className="w-full h-full object-cover" />}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                        <Play className="w-5 h-5 text-primary" fill="currentColor" />
                      </div>
                    </div>
                    <div className="absolute bottom-0 inset-x-0 h-1 bg-black/30">
                      <div className="h-full bg-primary" style={{ width: `${Math.min(100, (p.position_seconds / (p.duration_seconds || 1800)) * 100)}%` }} />
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="text-xs text-muted-foreground">Class {p.video?.class_level} · {p.video?.subject}</div>
                    <div className="text-sm font-semibold truncate mt-1">{p.video?.title}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search lessons…"
              data-testid="learnings-search" className="h-12 rounded-xl pl-11" />
          </div>
          <Select value={subject} onValueChange={setSubject}>
            <SelectTrigger className="h-12 rounded-xl" data-testid="learnings-subject"><SelectValue placeholder="All subjects" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All subjects</SelectItem>{SUBJECTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={cls} onValueChange={setCls}>
            <SelectTrigger className="h-12 rounded-xl" data-testid="learnings-class"><SelectValue placeholder="All classes" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All classes</SelectItem>{CLASSES.map(c => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="learnings-grid">
          {videos.map(v => {
            const p = progress[v.video_id];
            const pct = p ? Math.min(100, (p.position_seconds / (p.duration_seconds || v.duration_seconds || 1800)) * 100) : 0;
            return (
            <div key={v.video_id} className="glass rounded-2xl overflow-hidden card-3d group relative">
              <button onClick={() => openVideo(v)} data-testid={`video-card-${v.video_id}`} className="text-left w-full">
                <div className="relative aspect-video bg-muted">
                  {v.thumbnail && <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-xl">
                      <Play className="w-6 h-6 text-primary" fill="currentColor" />
                    </div>
                  </div>
                  {v.duration_seconds > 0 && (
                    <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/80 text-white text-[10px] font-medium flex items-center gap-1">
                      <Clock className="w-2.5 h-2.5" /> {Math.round(v.duration_seconds / 60)}m
                    </div>
                  )}
                  {pct > 0 && (
                    <div className="absolute bottom-0 inset-x-0 h-1 bg-black/40" data-testid={`video-progress-${v.video_id}`}>
                      <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-1">Class {v.class_level} · {v.subject}</div>
                  <div className="text-sm font-bold leading-tight line-clamp-2">{v.title}</div>
                </div>
              </button>
              <button onClick={() => toggleBm(v.video_id)} data-testid={`bookmark-video-${v.video_id}`}
                className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center backdrop-blur-md transition-colors ${bookmarks.has(v.video_id) ? "bg-primary text-primary-foreground" : "bg-white/80 text-foreground hover:bg-white"}`}>
                <Bookmark className="w-4 h-4" fill={bookmarks.has(v.video_id) ? "currentColor" : "none"} />
              </button>
            </div>
            );
          })}
        </div>
      </div>
      <VideoPlayerModal open={!!playing} onClose={() => setPlaying(null)} video={playing} onPlay={(v) => openVideo(v)} />
      <Footer />
    </div>
  );
}
